// src/components/ConnectionsList.jsx
import { useMemo } from 'react'
import { useDataStore } from '../stores/dataStore'
import ConnectionCard from './ConnectionCard'

export default function ConnectionsList() {
  const activeCount = useDataStore(s => s.connections.length)
  const connections = useDataStore(s => s.getVisibleConnections())
  const isLoading   = useDataStore(s => s.isLoading)
  const searchQuery = useDataStore(s => s.searchQuery).toLowerCase()
  const investigationCount = connections.filter(c => c.isRetained).length
  const filtered = useMemo(() => connections.filter(c => {
    if (!searchQuery) return true
    return (
      c.appName?.toLowerCase().includes(searchQuery) ||
      c.domain?.toLowerCase().includes(searchQuery) ||
      c.country?.toLowerCase().includes(searchQuery) ||
      c.remoteIP?.includes(searchQuery) ||
      c.service?.toLowerCase().includes(searchQuery)
    )
  }), [connections, searchQuery])

  if (isLoading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', flexDirection:'column', gap:12 }}>
        <div className="live-dot" style={{ width:10, height:10 }} />
        <div style={{ color:'var(--text-tertiary)', fontSize:13 }}>Scanning connections…</div>
      </div>
    )
  }

  if (filtered.length === 0) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', flexDirection:'column', gap:8 }}>
        <div style={{ fontSize:32 }}>🌐</div>
        <div style={{ color:'var(--text-secondary)', fontSize:14, fontWeight:500 }}>
          {searchQuery ? 'No connections match your search' : 'No active internet connections'}
        </div>
        <div style={{ color:'var(--text-tertiary)', fontSize:12 }}>
          {searchQuery ? 'Try a different search term' : 'Your Mac is offline or all connections are local'}
        </div>
      </div>
    )
  }

  return (
    <div style={{ height:'100%', overflowY:'auto', padding:'12px 16px 24px', display:'flex', flexDirection:'column', gap:6 }}>

      {/* ── Section header ── */}
      <div style={{ fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text-tertiary)', fontFamily:'var(--font-mono)', padding:'0 4px', marginBottom:2 }}>
        {activeCount} active {activeCount === 1 ? 'connection' : 'connections'}
        {investigationCount > 0 && ` · ${investigationCount} investigating`}
        {searchQuery && ` matching "${searchQuery}"`}
      </div>

      {/* ── Safari notice ── */}
      {activeCount > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderRadius:8, background:'rgba(77,157,224,0.05)', border:'1px solid rgba(77,157,224,0.15)', marginBottom:2 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--accent-blue)" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span style={{ fontSize:10, color:'var(--text-tertiary)', fontFamily:'var(--font-mono)', lineHeight:1.5 }}>
            Safari connections are hidden by macOS sandboxing. For full visibility use Chrome or Firefox.
          </span>
        </div>
      )}

      {/* ── Connection cards ── */}
      {filtered.map((conn, i) => (
        <ConnectionCard key={conn.id} connection={conn} index={i} />
      ))}
    </div>
  )
}
